import { useCallback, useEffect, useState } from 'react'
import useModelsStore from '../stores/models'
import usePrefStore from '../stores/prefs'
import useErrors from '../common/useErrors'
import useCollection from './useCollections'
import { AppWriteContext } from '../Contexts/AppWriteContext'
import type { TarifCurrencies, MinMaxPrice, ColorsResult, SizesResult, PriceResult, SKUParts } from '../types'
import { decideSkuVariant, type SkuVariantDecision } from '../libs/skuVariantGuard'

/** Valeur brute d'un modèle dans un tarif : prix direct, par couleur, ou par couleur+taille */
type TarifModelValue = number | Record<string, number> | Record<string, Record<string, number>> | null

const useTarifs = () => {
   const { emptyTarifs: _emptyTarifs, emptyTarifsChecksums: _emptyTarifsChecksums, tarifs, tarif_regions, devises } = useModelsStore()
   // Prefs store
   const { curTarifRegionName, curTarifCustomer, curTarifRegion, currentLang: curLang } = usePrefStore()
   //
   const { errCodes, buildError } = useErrors()
   //
   const { isHJOModel, splitSKU } = useCollection()
   //
   const [allMinMax, setAllMinMax] = useState<Record<string, MinMaxPrice>>({})
   //
   const { fetchTarifsForSingleRegion } = { ...AppWriteContext() } as any

   // Fonction de tri spéciale pour les tailles. Les tailles non trouvées sont mises après par ordre alpha
   const sizeSort = useCallback((a: string, b: string) => {
      const specialSizes: Record<string, string> = {
         // Astuce pour classer avant classement ordre alpha si pas trouvé
         XXXS: ' A',
         XXS: ' B',
         XS: ' C',
         S: ' D',
         SM: ' E',
         M: ' F',
         ML: ' G',
         L: ' H',
         LXL: ' I',
         XL: ' J',
         XXL: ' K',
         XXXL: ' L'
      }
      let valA, valB

      // Tout sera comparé en mode String, y compris les tailles numériques (qui sont sur 2 digits de toute façon)
      valA = specialSizes[a] ?? String(a)
      valB = specialSizes[b] ?? String(b)
      return valA.localeCompare(valB)
   }, [])

   // Fonction de tri spéciale pour les couleurs. Les couleurs non trouvées sont mises après, par ordre alpha
   const colorSort = useCallback((a: string, b: string) => {
      const specialColors: Record<string, string> = {
         // Astuce pour classer avant classement ordre alpha si pas trouvé
         WG: ' A',
         YG: ' B',
         PG: ' C',
         BG: ' D'
         // Florence me dit de ne pas trier le reste
      }
      let valA, valB

      // Tout sera comparé en mode String, ce qui est déjà le cas en principe
      valA = specialColors[a] ?? String(a)
      valB = specialColors[b] ?? String(b)
      return valA.localeCompare(valB)
   }, [])

   useEffect(() => {
      setAllMinMax({})
   }, [curTarifRegionName])

   /**
    * Retourne l'objet `currencies` d'une région, ou null si pas trouvée :
    * {
    *    hjo: { ces: { tarif: "CES001", currency: "EUR" }, ppu: {...} },
    *    other: {...}
    * }
    */
   const getTarifsForRegion = useCallback(
      (region_name: string | null): TarifCurrencies | null => {
         if (!tarif_regions || !region_name || !tarif_regions[region_name]) {
            return null
         } else {
            return tarif_regions[region_name].currencies
         }
      },
      [tarif_regions]
   )

   /**
    * Retrouve un nom de région à partir d'un nom de tarif OTHER_PPU et de sa devise. Exemple : ("PPU001", "EUR")
    * ou null si pas trouvé
    */
   const _findRegionFromTarifNames = useCallback(
      (from_cess: boolean, other_tarif: string | null, other_currency: string | null): string | null => {
         if (!tarif_regions) {
            return null
         } else {
            const found = Object.keys(tarif_regions).find((a_name) => {
               const a_tarif = tarif_regions[a_name]
               if (from_cess) {
                  return a_tarif.currencies.other.ces.tarif === other_tarif && a_tarif.currencies.other.ces.currency === other_currency
               } else {
                  return a_tarif.currencies.other.ppu.tarif === other_tarif && a_tarif.currencies.other.ppu.currency === other_currency
               }
            })
            return found ?? null
         }
      },
      [tarif_regions]
   )

   const findRegionFromCessionTarifNames = useCallback(
      (other_cess_tarif: string | null, other_cess_currency: string | null): string | null => {
         return _findRegionFromTarifNames(true, other_cess_tarif, other_cess_currency)
      },
      [_findRegionFromTarifNames]
   )

   const findRegionFromPublicTarifNames = useCallback(
      (other_ppu_tarif: string | null, other_ppu_currency: string | null): string | null => {
         return _findRegionFromTarifNames(false, other_ppu_tarif, other_ppu_currency)
      },
      [_findRegionFromTarifNames]
   )

   const emptyTarifs = () => {
      _emptyTarifs()
      _emptyTarifsChecksums()
      if (curTarifRegionName) {
         // Un timer de 1sec pour laisser le temps au ModelStore de se vider.
         setTimeout(() => {
            console.log('fetchTarifsForSingleRegion > GO')
            fetchTarifsForSingleRegion(
               curTarifRegionName,
               true,
               () => {
                  // true = force le DL du tarif sans tenir compte du checksum
                  console.log('fetchTarifsForSingleRegion > OK')
               },
               (err: unknown) => {
                  console.log('fetchTarifsForSingleRegion > ERR', err)
               }
            )
         }, 1000)
      }
   }

   /**
    * Vérifie la validité d'un tarif {hjo:{ces:...,ppu:...},other:...}
    * Retourne true/false
    */
   function isTarifValid(tarif: TarifCurrencies | null | undefined): boolean {
      if (!tarif) {
         return false
      }
      return (
         tarif.hjo.ces.tarif !== null &&
         tarif.hjo.ces.currency !== null &&
         tarif.hjo.ppu.tarif !== null &&
         tarif.hjo.ppu.currency !== null &&
         tarif.other.ces.tarif !== null &&
         tarif.other.ces.currency !== null &&
         tarif.other.ppu.tarif !== null &&
         tarif.other.ppu.currency !== null
      )
   }

   /**
    * Vérifie si un tarif de région est totalement chargé
    * Retourne true/false
    */
   const isTarifLoadedForRegion = useCallback(
      (region_name: string): boolean => {
         if (tarifs && tarif_regions && tarif_regions[region_name]) {
            const a_region = tarif_regions[region_name]

            return tarifs[a_region.currencies.hjo.ces.tarif] && tarifs[a_region.currencies.hjo.ppu.tarif] && tarifs[a_region.currencies.other.ces.tarif] && tarifs[a_region.currencies.other.ppu.tarif] ? true : false
         } else {
            return false
         }
      },
      [tarif_regions, tarifs]
   )

   /**
    * Vérifie si le tarif d'un client est identique à celui de sa région {other.[ppu & ces].[tarif & devise]}
    * Retourne true/false
    */
   function isCustomerTarifSameAsRegion(tarif_customer: TarifCurrencies | null, tarif_region: TarifCurrencies | null): boolean {
      if (!tarif_customer || !tarif_region) {
         return false
      }
      return (
         tarif_customer.other.ces.tarif === tarif_region.other.ces.tarif &&
         tarif_customer.other.ces.currency === tarif_region.other.ces.currency &&
         tarif_customer.other.ppu.tarif === tarif_region.other.ppu.tarif &&
         tarif_customer.other.ppu.currency === tarif_region.other.ppu.currency
      )
   }

   /**
    * @param tarif Nom du tarif (CES001...) ou null pour le tarif par défaut
    * @returns Objet ou null si tarif pas trouvé ou vide
    */
   const getTarifByName = useCallback(
      (tarif: string | null = null) => {
         const name = tarif ?? curTarifRegionName
         if (name && tarifs[name]) {
            return tarifs[name] || null
         } else {
            return null
         }
      },
      [curTarifRegionName, tarifs]
   )

   /**
    * Retourne un objet {PG: 9999, YG: 8888...}
    */
   const _getTarifForModel = useCallback(
      (forCession: boolean, model_id: string, region: string | null = null, currency: string | null = null): TarifModelValue => {
         const region_name = region ?? curTarifRegionName
         let currency_name: string | null | undefined
         let tarif_name: string | null | undefined = null

         if (isHJOModel(model_id)) {
            if (forCession) {
               if (!region && !currency && curTarifCustomer) {
                  tarif_name = curTarifCustomer.hjo.ces.tarif
                  currency_name = curTarifCustomer.hjo.ces.currency
               } else {
                  tarif_name = region_name ? tarif_regions[region_name]?.currencies.hjo.ces.tarif : null
                  currency_name = currency ?? (region_name ? tarif_regions[region_name]?.currencies.hjo.ces.currency : null)
               }
            } else {
               if (!region && !currency && curTarifCustomer) {
                  tarif_name = curTarifCustomer.hjo.ppu.tarif
                  currency_name = curTarifCustomer.hjo.ppu.currency
               } else {
                  tarif_name = region_name ? tarif_regions[region_name]?.currencies.hjo.ppu.tarif : null
                  currency_name = currency ?? (region_name ? tarif_regions[region_name]?.currencies.hjo.ppu.currency : null)
               }
            }
         } else {
            if (forCession) {
               if (!region && !currency && curTarifCustomer) {
                  tarif_name = curTarifCustomer.other.ces.tarif
                  currency_name = curTarifCustomer.other.ces.currency
               } else {
                  tarif_name = region_name ? tarif_regions[region_name]?.currencies.other.ces.tarif : null
                  currency_name = currency ?? (region_name ? tarif_regions[region_name]?.currencies.other.ces.currency : null)
               }
            } else {
               if (!region && !currency && curTarifCustomer) {
                  tarif_name = curTarifCustomer.other.ppu.tarif
                  currency_name = curTarifCustomer.other.ppu.currency
               } else {
                  tarif_name = region_name ? tarif_regions[region_name]?.currencies.other.ppu.tarif : null
                  currency_name = currency ?? (region_name ? tarif_regions[region_name]?.currencies.other.ppu.currency : null)
               }
            }
         }

         let obj_tarif: unknown = getTarifByName(tarif_name ?? null)

         if (!obj_tarif) {
            // console.log(`Tarif-région ${tarif_name} inconnu pour région ${region_name}`)
            return null
         } else {
            obj_tarif = (obj_tarif as Record<string, unknown>)[currency_name ?? '']
            if (!obj_tarif) {
               console.log(`Tarif-region ${tarif_name} pour région ${region_name} - Devise inconnue : currency ${currency}, ${currency_name}`)
               return null
            } else {
               obj_tarif = (obj_tarif as Record<string, unknown>)[model_id]
               if (!obj_tarif) {
                  return null
               } else {
                  return obj_tarif as TarifModelValue
               }
            }
         }
      },
      [curTarifCustomer, curTarifRegionName, getTarifByName, isHJOModel, tarif_regions]
   )

   /**
    * Prix CESSION
    */
   const getCessTarifForModel = useCallback(
      (model_id: string, region: string | null = null, currency: string | null = null): TarifModelValue => {
         return _getTarifForModel(true, model_id, region, currency)
      },
      [_getTarifForModel]
   )

   /**
    * Prix PPU
    */
   const getTarifForModel = useCallback(
      (model_id: string, region: string | null = null, currency: string | null = null): TarifModelValue => {
         return _getTarifForModel(false, model_id, region, currency)
      },
      [_getTarifForModel]
   )

   /**
    * Récupère les couleurs d'un modèle d'un tarif donné (ou actif si null) et une devise donnée (ou active si null)
    */
   const getColorsForModel = useCallback(
      (model_id: string, region: string | null = null, currency: string | null = null): ColorsResult => {
         const a_tarif = getTarifForModel(model_id, region, currency)

         if (a_tarif !== null) {
            if (typeof a_tarif === 'number') {
               // Cas 3 : modèle sans couleur
               return { colors: [], reason: null }
            } else if (a_tarif && typeof a_tarif === 'object') {
               const ret = Object.keys(a_tarif)
               return { colors: ret.sort(colorSort), reason: null }
            } else {
               return { colors: null, reason: buildError(errCodes.UNK_TYPE, `getColorsForModel - Unknown type for tarif/currency/model "${region}/${currency}/${model_id}"!`) }
            }
         } else {
            return { colors: null, reason: buildError(errCodes.UNK_TARIF, `getColorsForModel - Unknown tarif/currency/model "${region}/${currency}/${model_id}"!`) }
         }
      },
      [buildError, errCodes, getTarifForModel, colorSort]
   )

   /**
    * Récupère les tailles d'un modèle pour une couleur donnée d'un tarif donné (ou actif si null)
    */
   const getSizesForModelColor = useCallback(
      (model_id: string, color: string, region: string | null = null, currency: string | null = null): SizesResult => {
         let a_tarif = getTarifForModel(model_id, region, currency)

         if (a_tarif !== null) {
            if (typeof a_tarif === 'number') {
               return { sizes: null, reason: buildError(errCodes.NO_COLOR, `getSizesForModelColor - No color!`) }
            } else if (a_tarif && typeof a_tarif === 'object') {
               const colorTarif = (a_tarif as Record<string, unknown>)[color]
               if (colorTarif && typeof colorTarif === 'object') {
                  // Cas : {"44,45,..": 9999}
                  const ret = Object.keys(colorTarif).reduce<string[]>((prev, key) => {
                     return [...prev, ...key.split(',')].sort(sizeSort)
                  }, [])
                  return { sizes: ret, reason: null }
               } else if (typeof colorTarif === 'number') {
                  return { sizes: null, reason: null } // Ce n'est pas une erreur en soi...
               } else {
                  return { sizes: null, reason: buildError(errCodes.UNK_COLOR, `getSizesForModelColor - Unknown color "${color}"!`) }
               }
            } else {
               return { sizes: null, reason: buildError(errCodes.UNK_TYPE2, `getColorsForModel - Unknown type for tarif/currency/model "${region}/${currency}/${model_id}"!`) }
            }
         } else {
            return { sizes: null, reason: buildError(errCodes.UNK_TARIF2, `getColorsForModel - Unknown tarif/currency/model "${region}/${currency}/${model_id}"!`) }
         }
      },
      [buildError, errCodes, getTarifForModel, sizeSort]
   )

   /**
    * Récupère toutes les tailles d'un modèle (toutes couleurs confondues)
    */
   const getAllSizesForModel = useCallback(
      (model_id: string, region: string | null = null, currency: string | null = null): SizesResult => {
         const allColors = getColorsForModel(model_id, region, currency)

         if (allColors.reason) {
            return { sizes: null, reason: allColors.reason }
         } else {
            const ret = (allColors.colors ?? []).reduce<SizesResult>(
               (prev, color) => {
                  if (prev.reason) {
                     return prev
                  }
                  const obj_sizes = getSizesForModelColor(model_id, color, region, currency)

                  if (obj_sizes.reason) {
                     return { sizes: null, reason: obj_sizes.reason }
                  } else if (obj_sizes.sizes === null) {
                     return prev
                  } else {
                     return { sizes: [...(prev.sizes ?? []), ...obj_sizes.sizes], reason: null }
                  }
               },
               { sizes: [], reason: null }
            )
            if (ret.sizes) {
               return { sizes: [...new Set(ret.sizes)].sort(sizeSort), reason: ret.reason }
            } else {
               return ret
            }
         }
      },
      [getColorsForModel, getSizesForModelColor, sizeSort]
   )

   /**
    * Résout les couleurs/tailles disponibles pour model/color/size et délègue la décision
    * à decideSkuVariant (src/libs/skuVariantGuard.ts), seule source de la règle métier.
    */
   const sanitizeSkuVariant = useCallback(
      (model: string, color: string | null | undefined, size: string | null | undefined, region: string | null = null, currency: string | null = null): SkuVariantDecision => {
         const { colors } = getColorsForModel(model, region, currency)

         let decision = decideSkuVariant(color, size, { colors, sizes: null })
         let sizes: string[] | null = null

         if (decision.color !== null && decision.ok && !decision.undetermined) {
            // Couleur retenue : on peut résoudre ses tailles et recalculer la décision finale.
            ;({ sizes } = getSizesForModelColor(model, decision.color, region, currency))
            decision = decideSkuVariant(color, size, { colors, sizes })
         }

         if (!decision.undetermined && (decision.dropped.length > 0 || !decision.ok)) {
            console.warn('[skuVariantGuard]', { model, color, size, colors, sizes, decision })
         }

         return decision
      },
      [getColorsForModel, getSizesForModelColor]
   )

   /**
    * Récupère le prix d'un modèle pour une couleur et une taille d'un tarif donné (ou actif si null)
    */
   const _getPriceForModelColorSize = useCallback(
      (forCession: boolean, model_id: string, color: string | null, size: string | null, region: string | null = null, currency: string | null = null): PriceResult => {
         let a_tarif: TarifModelValue

         if (forCession) {
            a_tarif = getCessTarifForModel(model_id, region, currency)
         } else {
            a_tarif = getTarifForModel(model_id, region, currency)
         }

         console.log('getPriceForModelColorSize', a_tarif)

         if (a_tarif !== null) {
            if (color) {
               // On cherche une couleur donnée
               const colorTarif = (a_tarif as Record<string, unknown>)[color]
               if (colorTarif !== undefined) {
                  if (size) {
                     // Cas 1 : {"44,45,..": 9999}
                     const ret = Object.keys(colorTarif as Record<string, unknown>).reduce<number | null>((prev, key) => {
                        const price_for_sizes = (colorTarif as Record<string, number>)[key]
                        if (key.split(',').includes(size)) {
                           return price_for_sizes
                        } else {
                           return prev
                        }
                     }, null)
                     return { price: ret, reason: ret ? null : buildError(errCodes.UNK_SIZE, `getPriceForModelColorSize - Unknown size "${size}"`) }
                  } else {
                     // On ne cherche pas une taille donnée
                     if (typeof colorTarif !== 'number') {
                        return { price: null, reason: buildError(errCodes.MISS_SIZE, `getPriceForModelColorSize - Missing size argument!`) }
                     } else {
                        return { price: colorTarif, reason: null }
                     }
                  }
               } else {
                  return { price: null, reason: buildError(errCodes.UNK_COLOR2, `getPriceForModelColorSize - Unknown color "${color}"`) }
               }
            } else {
               // On ne cherche pas une couleur donnée
               if (typeof a_tarif !== 'number') {
                  return { price: null, reason: buildError(errCodes.NO_COLOR2, `getPriceForModelColorSize - Missing color argument!`) }
               } else {
                  return { price: a_tarif, reason: null }
               }
            }
         } else {
            return { price: null, reason: buildError(errCodes.UNK_TARIF3, `getPriceForModelColorSize - Unknown tarif/currency/model "${region}/${currency}/${model_id}"!`) }
         }
      },
      [getTarifForModel, getCessTarifForModel, buildError, errCodes]
   )

   /**
    * Prix PPU
    */
   const getPriceForModelColorSize = useCallback(
      (model_id: string, color: string | null, size: string | null, region: string | null = null, currency: string | null = null): PriceResult => {
         return _getPriceForModelColorSize(false, model_id, color, size, region, currency)
      },
      [_getPriceForModelColorSize]
   )

   /**
    * Prix CESSION
    */
   const getCessPriceForModelColorSize = useCallback(
      (model_id: string, color: string | null, size: string | null, region: string | null = null, currency: string | null = null): PriceResult => {
         return _getPriceForModelColorSize(true, model_id, color, size, region, currency)
      },
      [_getPriceForModelColorSize]
   )

   const _getCurrency = useCallback(
      (type: 'ppu' | 'ces'): string | null => {
         let name: string | null
         const lang = curLang === 'en' ? 'en' : 'fr'

         if (curTarifCustomer) {
            name = curTarifCustomer.other[type].currency
         } else if (curTarifRegion) {
            name = curTarifRegion.other[type].currency
         } else {
            return null
         }
         if (devises && name) {
            const label = devises[name]?.label
            if (typeof label === 'object' && label !== null) {
               return (label as Record<string, string>)[lang] ?? name
            }
            return name
         } else {
            return name
         }
      },
      [curLang, curTarifCustomer, curTarifRegion, devises]
   )

   /**
    * Prix PPU via SKU
    */
   const getPriceForModelBySKU = useCallback(
      (sku: string, region: string | null = null, currency: string | null = null): PriceResult => {
         console.log(splitSKU(sku))
         const parts = splitSKU(sku) as SKUParts | null
         const { model, color, size } = { ...parts }

         return getPriceForModelColorSize(model ?? '', color ?? null, size ?? null, region, currency)
      },
      [getPriceForModelColorSize, splitSKU]
   )

   /**
    * Prix CESSION via SKU
    */
   const getCessPriceForModelBySKU = useCallback(
      (sku: string, region: string | null = null, currency: string | null = null): PriceResult => {
         const parts = splitSKU(sku) as SKUParts | null
         const { model, color, size } = { ...parts }

         return getCessPriceForModelColorSize(model ?? '', color ?? null, size ?? null, region, currency)
      },
      [getCessPriceForModelColorSize, splitSKU]
   )

   /**
    * Retourne le nom de la devise PPU en tenant compte du curCustomer si existe, sinon du curRegion
    */
   const getPPUCurrency = useCallback((): string | null => {
      return _getCurrency('ppu')
   }, [_getCurrency])

   /**
    * Retourne le nom de la devise CES en tenant compte du curCustomer si existe, sinon du curRegion
    */
   const getCESCurrency = useCallback((): string | null => {
      return _getCurrency('ces')
   }, [_getCurrency])

   /**
    * Calcule les prix mini et maxi d'un modèle pour un tarif donné (ou actif si null)
    */
   const _getMinMaxPriceForModel = useCallback(
      (forCession: boolean, model_id: string, region: string | null = null, currency: string | null = null): MinMaxPrice => {
         const region_name = region ?? curTarifRegionName
         let currency_name: string | null | undefined
         let a_tarif: TarifModelValue
         let min_max_idx: string
         let ret: MinMaxPrice

         if (forCession) {
            a_tarif = getCessTarifForModel(model_id, region, currency)
            if (!region && !currency && curTarifCustomer) {
               currency_name = curTarifCustomer.other.ces.currency
            } else {
               currency_name = currency ?? (region_name ? tarif_regions[region_name]?.currencies.other.ces.currency : null)
            }
            min_max_idx = `${model_id}_${region}_${currency}_CES`
         } else {
            a_tarif = getTarifForModel(model_id, region, currency)
            if (!region && !currency && curTarifCustomer) {
               currency_name = curTarifCustomer.other.ppu.currency
            } else {
               currency_name = currency ?? (region_name ? tarif_regions[region_name]?.currencies.other.ppu.currency : null)
            }
            min_max_idx = `${model_id}_${region}_${currency}_PP`
         }

         const found = allMinMax[min_max_idx]
         if (found) {
            return found
         }

         if (a_tarif !== null && typeof a_tarif === 'object') {
            ret = Object.keys(a_tarif).reduce<MinMaxPrice>(
               (prev, key) => {
                  const value = (a_tarif as Record<string, unknown>)[key]
                  if (typeof value === 'number') {
                     return { ...prev, min: Math.min(prev.min ?? value, value), max: Math.max(prev.max ?? value, value) }
                  } else if (value && typeof value === 'object') {
                     const sub1 = Object.keys(value as Record<string, unknown>).reduce<MinMaxPrice>(
                        (prev, key) => {
                           const value2 = (value as Record<string, unknown>)[key]
                           if (typeof value2 === 'number') {
                              return { ...prev, min: Math.min(prev.min ?? value2, value2), max: Math.max(prev.max ?? value2, value2) }
                           } else if (value2 && typeof value2 === 'object') {
                              const sub2 = Object.keys(value2 as Record<string, unknown>).reduce<MinMaxPrice>(
                                 (prev, key) => {
                                    const value3 = (value2 as Record<string, unknown>)[key]
                                    if (typeof value3 !== 'number') {
                                       console.log('Type inconnu (3) : ', region, region_name, ' - Devise', currency, currency_name, ' - Modèle :', model_id, ' - Type :', typeof value3, value3)
                                       return { ...prev, reason: buildError(errCodes.UNK_TYPE3, `getMinMaxPriceForModel - Unknown tarif type [3/${value3}]`) }
                                    } else {
                                       return { ...prev, min: Math.min(prev.min ?? value3, value3), max: Math.max(prev.max ?? value3, value3) }
                                    }
                                 },
                                 { min: null, max: null, reason: null }
                              )
                              return { ...prev, min: Math.min(prev.min ?? sub2.min ?? 0, sub2.min ?? 0), max: Math.max(prev.max ?? sub2.max ?? 0, sub2.max ?? 0) }
                           } else {
                              console.log('Type inconnu (2) : ', region, region_name, ' - Devise', currency, currency_name, ' - Modèle :', model_id, ' - Type :', typeof value2, value2)
                              return { ...prev, reason: buildError(errCodes.UNK_TYPE4, `getMinMaxPriceForModel - Unknown tarif type [2/${value2}]`) }
                           }
                        },
                        { min: null, max: null, reason: null }
                     )
                     return { ...prev, min: Math.min(prev.min ?? sub1.min ?? 0, sub1.min ?? 0), max: Math.max(prev.max ?? sub1.max ?? 0, sub1.max ?? 0) }
                  } else {
                     console.log('Type inconnu (1) : ', region, region_name, ' - Devise', currency, currency_name, ' - Modèle :', model_id, ' - Type :', typeof value, value)
                     return { ...prev, reason: buildError(errCodes.UNK_TYPE5, `getMinMaxPriceForModel - Unknown tarif type [1${value}]`) }
                  }
               },
               { min: null, max: null, reason: null }
            )
         } else if (typeof a_tarif === 'number') {
            ret = { min: a_tarif, max: a_tarif, reason: null }
         } else {
            ret = { min: null, max: null, reason: buildError(errCodes.UNK_TARIF4, `getMinMaxPriceForModel - Unknown tarif ${region_name}/${currency_name}/${model_id}`) }
         }
         setAllMinMax((cur) => {
            return { ...cur, [min_max_idx]: ret }
         })
         return ret
      },
      [allMinMax, buildError, curTarifCustomer, curTarifRegionName, errCodes, getCessTarifForModel, getTarifForModel, tarif_regions]
   )

   /**
    * Prix CESSION
    */
   const getMinMaxCessPriceForModel = useCallback(
      (model_id: string, region: string | null = null, currency: string | null = null): MinMaxPrice => {
         return _getMinMaxPriceForModel(true, model_id, region, currency)
      },
      [_getMinMaxPriceForModel]
   )

   /**
    * Prix PPU
    */
   const getMinMaxPriceForModel = useCallback(
      (model_id: string, region: string | null = null, currency: string | null = null): MinMaxPrice => {
         return _getMinMaxPriceForModel(false, model_id, region, currency)
      },
      [_getMinMaxPriceForModel]
   )

   /**
    * Calcule les prix mini et maxi de chaque modèle d'une liste de modèles
    */
   const _getMinMaxPriceForEachModelInArray = useCallback(
      (forCession: boolean, models: Array<{ id: string }>, region: string | null = null, currency: string | null = null): Record<string, MinMaxPrice> | null => {
         if (!Array.isArray(models)) {
            console.error('getMinMaxPriceForEachModelInArray - Not array!', models)
            return null
         }
         return models.reduce<Record<string, MinMaxPrice>>((prev, model) => {
            let miniMax: MinMaxPrice

            if (forCession) {
               miniMax = getMinMaxCessPriceForModel(model.id, region, currency)
            } else {
               miniMax = getMinMaxPriceForModel(model.id, region, currency)
            }
            return {
               ...prev,
               [model.id]: { min: miniMax.min, max: miniMax.max, reason: null }
            }
         }, {})
      },
      [getMinMaxCessPriceForModel, getMinMaxPriceForModel]
   )

   /**
    * Prix CESSION - par tableau de modèles
    */
   const getMinMaxCessPriceForEachModelInArray = useCallback(
      (models: Array<{ id: string }>, region: string | null = null, currency: string | null = null) => {
         return _getMinMaxPriceForEachModelInArray(true, models, region, currency)
      },
      [_getMinMaxPriceForEachModelInArray]
   )

   /**
    * Prix PPU - par tableau de modèles
    */
   const getMinMaxPriceForEachModelInArray = useCallback(
      (models: Array<{ id: string }>, region: string | null = null, currency: string | null = null) => {
         return _getMinMaxPriceForEachModelInArray(false, models, region, currency)
      },
      [_getMinMaxPriceForEachModelInArray]
   )

   return {
      getTarifsForRegion,
      findRegionFromCessionTarifNames,
      findRegionFromPublicTarifNames,
      emptyTarifs,
      isTarifValid,
      isTarifLoadedForRegion,
      isCustomerTarifSameAsRegion,
      getTarifByName,
      getTarifForModel,
      getCessTarifForModel,
      getMinMaxPriceForModel,
      getMinMaxCessPriceForModel,
      getMinMaxPriceForEachModelInArray,
      getMinMaxCessPriceForEachModelInArray,
      getPriceForModelColorSize,
      getCessPriceForModelColorSize,
      getPriceForModelBySKU,
      getCessPriceForModelBySKU,
      getPPUCurrency,
      getCESCurrency,
      getColorsForModel,
      getSizesForModelColor,
      getAllSizesForModel,
      sanitizeSkuVariant
   }
}

export default useTarifs
